/**
 * Self check-in — host přijde přes link sobacov.nfi-iq.cz/#checkin?r=ID&pin=PIN
 * Vyplní domovní knihu (sebe + spolucestující) → dostane kód na dveře.
 * § 101/102 zák. 326/1999 Sb.
 */
import { useEffect, useState } from 'react'
import { apiUrl } from '../lib/api'

type Guest = {
  first_name: string; last_name: string; birth_date: string
  nationality: string; document_type: string; document_number: string
  address_street: string; address_city: string; address_zip: string; address_country: string
  purpose: string; visa_number: string
}

const emptyGuest = (): Guest => ({
  first_name: '', last_name: '', birth_date: '', nationality: 'CZ',
  document_type: 'op', document_number: '',
  address_street: '', address_city: '', address_zip: '', address_country: 'CZ',
  purpose: '', visa_number: '',
})

function parseQuery() {
  const h = window.location.hash
  const q = h.includes('?') ? h.split('?')[1] : ''
  const p = new URLSearchParams(q)
  return { r: p.get('r') || '', pin: p.get('pin') || '' }
}

export function CheckIn() {
  const { r, pin } = parseQuery()
  const [info, setInfo] = useState<any>(null)
  const [err, setErr] = useState<string | null>(null)
  const [guests, setGuests] = useState<Guest[]>([emptyGuest()])
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [doorPin, setDoorPin] = useState<string | null>(null)

  useEffect(() => {
    if (!r || !pin) { setErr('Chybí kód rezervace v odkazu.'); return }
    fetch(apiUrl(`/api/sobacov/checkin/${r}?pin=${encodeURIComponent(pin)}`))
      .then(res => res.ok ? res.json() : Promise.reject(new Error('Rezervace nenalezena nebo špatný kód.')))
      .then((d) => {
        setInfo(d)
        // Předvyplň počet hostů
        if (d.num_persons && d.num_persons > 1) {
          setGuests(Array.from({ length: d.num_persons }, () => emptyGuest()))
        }
      })
      .catch((e) => setErr(e.message))
  }, [r, pin])

  function upd(i: number, field: keyof Guest, val: string) {
    setGuests(gs => gs.map((g, idx) => idx === i ? { ...g, [field]: val } : g))
  }

  async function submit() {
    setBusy(true); setErr(null)
    try {
      const valid = guests.filter(g => g.first_name.trim() && g.last_name.trim())
      if (valid.length === 0) throw new Error('Vyplňte alespoň jednoho hosta.')
      if (!consent) throw new Error('Nutný souhlas se zpracováním údajů.')
      const res = await fetch(apiUrl(`/api/sobacov/checkin/${r}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, consent_gdpr: consent, guests: valid }),
      })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.detail || 'Check-in se nezdařil.')
      }
      const j = await res.json()
      setDoorPin(j.door_pin)
    } catch (e: any) { setErr(e.message) }
    finally { setBusy(false) }
  }

  // ── Hotovo ──
  if (doorPin) {
    return (
      <section className="min-h-screen bg-steel-700 text-white flex items-center justify-center p-4">
        <div className="bg-white text-ink-800 max-w-md w-full p-8 text-center">
          <div className="text-5xl text-emerald-500 mb-3">✓</div>
          <h1 className="font-extrabold text-2xl tracking-tight">Check-in dokončen</h1>
          <p className="text-ink-700 mt-3 text-sm">Váš kód pro vstup:</p>
          <div className="my-5 text-5xl font-bold tracking-[0.3em] text-brand-500">{doorPin}</div>
          <p className="text-sm text-ink-700">
            Tímto kódem otevřete dveře vašeho ubytování. Uschovejte si ho.
          </p>
          <p className="text-xs text-ink-700/70 mt-4">
            Přejeme příjemný pobyt. Team Sobáčov · +420 775 140 705
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="min-h-screen bg-steel-700 text-white py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <p className="h-eyebrow text-brand-400">Online check-in</p>
          <h1 className="font-extrabold text-3xl mt-3 tracking-tight">Sportovní areál Sobáčov</h1>
          <span className="block w-14 h-[2px] bg-brand-500 mx-auto mt-4" />
        </div>

        {err && !info && (
          <div className="bg-brand-50 text-brand-600 p-4 text-center">{err}</div>
        )}

        {info && (
          <div className="bg-white text-ink-800 p-6 md:p-8">
            {/* Souhrn rezervace */}
            <div className="bg-brand-50 border border-brand-200 p-4 mb-5">
              <div className="text-[10px] uppercase tracking-[0.20em] text-brand-500 font-bold">Vaše rezervace</div>
              <div className="font-semibold text-ink-900 mt-1">{info.guest_name}</div>
              <div className="text-sm text-ink-700">
                {info.resource_label ? info.resource_label + ' · ' : ''}
                {info.from_dt && new Date(info.from_dt).toLocaleDateString('cs-CZ')}
                {' – '}
                {info.to_dt && new Date(info.to_dt).toLocaleDateString('cs-CZ')}
              </div>
            </div>

            {info.already_checked_in && (
              <div className="bg-amber-50 text-amber-700 p-3 mb-4 text-sm">
                ⚠️ Tato rezervace už byla odbavena. Novým odesláním údaje přepíšete.
              </div>
            )}

            <p className="text-sm text-ink-700 mb-4">
              Ze zákona (§ 101 zák. 326/1999 Sb.) evidujeme všechny ubytované. Vyplňte prosím
              údaje za sebe i spolucestující.
            </p>

            {guests.map((g, i) => (
              <div key={i} className="border border-ink-900/10 p-4 mb-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-[0.20em] text-brand-500 font-bold">
                    Host {i + 1}
                  </div>
                  {guests.length > 1 && (
                    <button onClick={() => setGuests(gs => gs.filter((_, x) => x !== i))}
                      className="text-ink-700/60 hover:text-brand-500 text-sm">✕ odebrat</button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <F label="Jméno *" v={g.first_name} on={(v) => upd(i, 'first_name', v)} />
                  <F label="Příjmení *" v={g.last_name} on={(v) => upd(i, 'last_name', v)} />
                  <F label="Datum narození" type="date" v={g.birth_date} on={(v) => upd(i, 'birth_date', v)} />
                  <div>
                    <Lbl>Státní občanství</Lbl>
                    <select value={g.nationality} onChange={(e) => upd(i, 'nationality', e.target.value)}
                      className="mt-1 w-full border border-ink-900/15 px-2 py-2 text-sm">
                      <option value="CZ">Česká republika</option>
                      <option value="SK">Slovensko</option>
                      <option value="DE">Německo</option>
                      <option value="PL">Polsko</option>
                      <option value="AT">Rakousko</option>
                      <option value="UA">Ukrajina</option>
                      <option value="OTHER">Jiné</option>
                    </select>
                  </div>
                  <div>
                    <Lbl>Doklad</Lbl>
                    <select value={g.document_type} onChange={(e) => upd(i, 'document_type', e.target.value)}
                      className="mt-1 w-full border border-ink-900/15 px-2 py-2 text-sm">
                      <option value="op">Občanský průkaz</option>
                      <option value="pas">Cestovní pas</option>
                      <option value="jiny">Jiný</option>
                    </select>
                  </div>
                  <F label="Číslo dokladu" v={g.document_number} on={(v) => upd(i, 'document_number', v)} />
                  <F label="Ulice + č.p." v={g.address_street} on={(v) => upd(i, 'address_street', v)} />
                  <F label="Město" v={g.address_city} on={(v) => upd(i, 'address_city', v)} />
                  <F label="PSČ" v={g.address_zip} on={(v) => upd(i, 'address_zip', v)} />
                </div>
                {/* Cizinci — účel pobytu + vízum */}
                {g.nationality !== 'CZ' && (
                  <div className="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-ink-900/8">
                    <F label="Účel pobytu" v={g.purpose} on={(v) => upd(i, 'purpose', v)} placeholder="turistika, práce…" />
                    <F label="Číslo víza/povolení" v={g.visa_number} on={(v) => upd(i, 'visa_number', v)} />
                  </div>
                )}
              </div>
            ))}

            <button onClick={() => setGuests(gs => [...gs, emptyGuest()])}
              className="text-brand-500 text-sm font-semibold mb-4">+ Přidat dalšího hosta</button>

            <label className="flex items-start gap-2 bg-ink-50 p-3 mb-4 cursor-pointer">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-5 h-5 mt-0.5" />
              <span className="text-xs text-ink-700">
                Souhlasím se zpracováním osobních údajů pro účely evidence ubytovaných dle zákona
                (domovní kniha, hlášení Policii ČR u cizinců) a místního poplatku z pobytu. Údaje
                uchováváme 6 let dle § 102 zák. 326/1999 Sb.
              </span>
            </label>

            {err && <div className="bg-brand-50 text-brand-600 p-2 text-sm mb-3">{err}</div>}

            <button onClick={submit} disabled={busy || !consent}
              className="btn-book w-full disabled:opacity-40">
              {busy ? 'Odesílám…' : 'Dokončit check-in'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

function Lbl({ children }: { children: any }) {
  return <span className="text-[10px] uppercase tracking-[0.15em] text-brand-500 font-bold">{children}</span>
}
function F({ label, v, on, type = 'text', placeholder }: {
  label: string; v: string; on: (v: string) => void; type?: string; placeholder?: string
}) {
  return (
    <label className="block">
      <Lbl>{label}</Lbl>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} placeholder={placeholder}
        className="mt-1 w-full border border-ink-900/15 px-2 py-2 text-sm focus:outline-none focus:border-brand-500" />
    </label>
  )
}
